/**
 * Сборка тел сообщений для действий пользователя.
 *
 * Каждое действие в протоколе — это сообщение в блокчейн, и его тело собирает
 * клиент. Ошибка в раскладке означает не «кнопка не работает», а отправленные
 * в контракт деньги, которые он не понял: платёж, не зачтённый в график, или
 * перевод, прочитанный как что-то другое.
 *
 * Поэтому раскладки здесь повторяют структуры из contracts/types.tolk поле в
 * поле, а порядок полей продублирован комментариями. Tolk сериализует поля в
 * порядке объявления — любая перестановка в контракте обязана быть повторена
 * здесь.
 */

import { Address, beginCell, type Cell } from '@ton/core';
import {
  OP_ASSIGN_POSITION,
  OP_BUY_POSITION,
  OP_DEFERRAL,
  OP_LIST_POSITION,
  OP_CANCEL_DEAL,
  OP_CLAIM_DEFAULT,
  OP_CREATE_DEAL,
  OP_NFT_TRANSFER,
  OP_PAY,
  OP_SET_COLLECTION,
  type ItemLayout,
  OP_SET_DEAL_CODE,
  OP_SET_FEE,
  OP_ACCEPT_ADMIN,
  OP_SET_NEXT_ADMIN,
  OP_SET_REF_PUBKEY,
  OP_WITHDRAW_FEES,
  OP_WITHDRAW_OWED,
  SIGNATURE_BITS,
} from './opcodes.ts';
import type { RefundTier } from './schedule.ts';

/** Передача NFT в залог: айтем должен успеть уведомить сделку. */
export const NFT_TRANSFER_TON = 300_000_000n;
/** Простые сообщения самой сделке: claim_default, cancel, withdraw. */
export const DEAL_MESSAGE_TON = 300_000_000n;
/**
 * Газ на уведомление сделке о залоге. Без него гифт доедет до контракта, но
 * сделка о нём не узнает и оффер не появится.
 */
export const FORWARD_TON = 100_000_000n;
export const CREATE_DEAL_TON = 300_000_000n;

/**
 * Приглашение к предоплате: адрес приглашающего и подпись протокола над
 * парой (приглашающий, покупатель).
 *
 * Отдельной ячейкой: 267 бит адреса и 512 бит подписи в теле сообщения
 * рядом с прочим не помещаются при лимите 1023.
 */
export function buildReferralPayload(referrer: string, signature: Buffer): Cell {
  if (signature.length !== SIGNATURE_BITS / 8) {
    throw new Error(`подпись приглашения обязана быть ${SIGNATURE_BITS / 8} байт`);
  }
  return beginCell().storeAddress(Address.parse(referrer)).storeBuffer(signature).endCell();
}

export function referralSignedCell(referrer: string, buyer: string): Cell {
  return beginCell()
    .storeAddress(Address.parse(referrer))
    .storeAddress(Address.parse(buyer))
    .endCell();
}

/**
 * Платёж по сделке.
 *
 * Деньги едут значением сообщения, тело несёт только опкод и, для
 * предоплаты, приглашение. Сумму к отправке считает вызывающий: это
 * `amount + PAYMENT_GAS`, и надбавку человек видит до подтверждения.
 */
export function buildPayment(opts: {
  /**
   * Приглашение — только для предоплаты: контракт читает его один раз, при
   * переходе сделки в активную. В остальных платежах оно игнорируется,
   * поэтому и посылать его незачем.
   */
  referral?: { referrer: string; signature: Buffer };
  queryId?: bigint;
}): Cell {
  const b = beginCell().storeUint(OP_PAY, 32).storeUint(opts.queryId ?? 0n, 64);
  if (!opts.referral) {
    return b.storeMaybeRef(null).endCell();
  }
  return b
    .storeMaybeRef(buildReferralPayload(opts.referral.referrer, opts.referral.signature))
    .endCell();
}

/**
 * Перенос платежа. Отличается от обычного платежа опкодом — и тем, что эта
 * сумма НЕ идёт в счёт долга.
 */
export function buildDeferral(queryId = 0n): Cell {
  return beginCell().storeUint(OP_DEFERRAL, 32).storeUint(queryId, 64).endCell();
}

/**
 * Выкуп позиции продавца. §8
 *
 * Сделка пересылает эти деньги прежнему владельцу позиции и переписывает
 * право на оставшиеся платежи на плательщика, в одной транзакции: заплатить
 * и не получить позицию невозможно, как и наоборот.
 */
export function buildBuyPosition(queryId = 0n): Cell {
  return beginCell().storeUint(OP_BUY_POSITION, 32).storeUint(queryId, 64).endCell();
}

/**
 * Передача NFT в залог. Отправляется контракту айтема.
 *
 * forward_ton_amount обязан быть положительным: именно он оплачивает
 * уведомление сделке, без которого залог не будет зафиксирован.
 */
export function buildLockNft(opts: {
  dealAddress: string;
  responseTo: string;
  queryId?: bigint;
}): Cell {
  // Порядок: op, query_id, new_owner, response_destination, custom_payload,
  // forward_amount, forward_payload
  return beginCell()
    .storeUint(OP_NFT_TRANSFER, 32)
    .storeUint(opts.queryId ?? 0n, 64)
    .storeAddress(Address.parse(opts.dealAddress))
    .storeAddress(Address.parse(opts.responseTo))
    .storeMaybeRef(null)
    .storeCoins(FORWARD_TON)
    .storeBit(false)
    .endCell();
}

export interface DealRequestInput {
  totalPrice: bigint;
  upfront: bigint;
  deferFee: bigint;
  /** Секунды между платежами. */
  period: number;
  deadline: number;
  nPayments: number;
  refundTier: RefundTier;
  maxDeferrals: number;
}

/** Условия сделки отдельной ячейкой — так их ждёт мастер. */
function buildDealRequest(r: DealRequestInput): Cell {
  // Порядок: struct DealRequest в contracts/types.tolk
  return beginCell()
    .storeCoins(r.totalPrice)
    .storeCoins(r.upfront)
    .storeCoins(r.deferFee)
    .storeUint(r.period, 32)
    .storeUint(r.deadline, 32)
    .storeUint(r.nPayments, 8)
    .storeUint(r.refundTier, 8)
    .storeUint(r.maxDeferrals, 8)
    .endCell();
}

/**
 * Создание сделки. Отправляется мастеру.
 *
 * Комиссию продавец не задаёт — мастер проставляет её сам из своего слепка,
 * поэтому в условиях её и нет.
 */
export function buildCreateDeal(opts: {
  collection: string;
  nftAddress: string;
  nftIndex: bigint;
  nonce: bigint;
  request: DealRequestInput;
  queryId?: bigint;
}): Cell {
  return beginCell()
    .storeUint(OP_CREATE_DEAL, 32)
    .storeUint(opts.queryId ?? 0n, 64)
    .storeAddress(Address.parse(opts.collection))
    .storeAddress(Address.parse(opts.nftAddress))
    .storeUint(opts.nftIndex, 256)
    .storeUint(opts.nonce, 64)
    .storeRef(buildDealRequest(opts.request))
    .endCell();
}

/** Забрать NFT у сорвавшейся сделки. Дёрнуть может кто угодно. */
export function buildClaimDefault(queryId = 0n): Cell {
  return beginCell().storeUint(OP_CLAIM_DEFAULT, 32).storeUint(queryId, 64).endCell();
}

/** Отмена продавцом до первого платежа. */
export function buildCancelDeal(queryId = 0n): Cell {
  return beginCell().storeUint(OP_CANCEL_DEAL, 32).storeUint(queryId, 64).endCell();
}

/** Передача стороны продавца — продажа контракта. */
export function buildAssignPosition(newOwner: string, queryId = 0n): Cell {
  return beginCell()
    .storeUint(OP_ASSIGN_POSITION, 32)
    .storeUint(queryId, 64)
    .storeAddress(Address.parse(newOwner))
    .endCell();
}

/**
 * Выставить позицию на продажу или снять с неё. §8
 *
 * Цена хранится в самой сделке: «продаётся за N» должно быть проверяемым
 * утверждением, а не обещанием сервера.
 */
export function buildListPosition(price: bigint, queryId = 0n): Cell {
  return beginCell()
    .storeUint(OP_LIST_POSITION, 32)
    .storeUint(queryId, 64)
    .storeCoins(price)
    .endCell();
}

/** Забрать зависшую выплату. Уходит только по записанному адресу. */
export function buildWithdrawOwed(queryId = 0n): Cell {
  return beginCell().storeUint(OP_WITHDRAW_OWED, 32).storeUint(queryId, 64).endCell();
}

/** Сообщение для TON Connect: адрес, сумма в нанотонах и тело в base64. */
export interface TxMessage {
  address: string;
  amount: string;
  payload: string;
}

export function toTxMessage(to: string, tonAmount: bigint, body: Cell): TxMessage {
  return {
    // Дружественная форма обязательна: TON Connect с версии 3.4 проверяет
    // сообщения перед отправкой и сырой «0:bcae…» отвергает целиком —
    // «Wrong 'address' format in message at index 0». Внутри у нас всё сырое
    // (это ключ в базе), и наружу такие адреса уезжали как есть: залог гифта
    // и любое действие по сделке падали, не дойдя до кошелька.
    //
    // Bounceable — потому что на той стороне контракт: если он сообщение не
    // примет, деньги обязаны вернуться, а не осесть на его балансе.
    address: Address.parse(to).toString({ urlSafe: true, bounceable: true }),
    amount: tonAmount.toString(),
    payload: body.toBoc().toString('base64'),
  };
}

// ---------------------------------------------------------------------------
// Админские операции мастера
// ---------------------------------------------------------------------------
//
// Раскладки повторяют structs из contracts/types.tolk. Ошибка здесь дороже,
// чем в пользовательских сообщениях: админ отправляет их редко, заметить
// расхождение некому, а последствие — изменённая комиссия или коллекция,
// открытая не тем кодом айтема.

/**
 * Смена комиссии. Контракт всё равно проверит хардкап в 5% — здесь проверка
 * ради внятного отказа до отправки, а не вместо контрактной.
 */
export function buildSetFee(feeBps: number, queryId = 0n): Cell {
  return beginCell()
    .storeUint(OP_SET_FEE, 32)
    .storeUint(queryId, 64)
    .storeUint(feeBps, 16)
    .endCell();
}

/**
 * Коллекция в реестре. `itemCode === null` убирает её.
 *
 * Код айтема нужен потому, что мастер считает адрес экземпляра сам:
 * синхронно спросить коллекцию в TON нельзя, а верить адресу из запроса —
 * значит принимать в залог что угодно.
 *
 * `layout` говорит, что коллекция кладёт в начальные данные айтема. Одного
 * кода мало: эталонная TEP-62 кладёт (index: uint64, collection), TON DNS —
 * 256-битный индекс, гифты Fragment — ссылку на статику. Ошибиться нельзя
 * незаметно: адрес либо сходится с настоящим айтемом, либо нет.
 */
export function buildSetCollection(
  collection: string,
  itemCode: Cell | null,
  layout: ItemLayout = 0,
  queryId = 0n,
): Cell {
  return beginCell()
    .storeUint(OP_SET_COLLECTION, 32)
    .storeUint(queryId, 64)
    .storeAddress(Address.parse(collection))
    .storeMaybeRef(itemCode)
    .storeUint(layout, 8)
    .endCell();
}

/**
 * Ключ, которым протокол подписывает приглашения. Ноль выключает рефералку:
 * подпись перестаёт сходиться, и новые сделки идут как обычные.
 *
 * Слепком, как и комиссия: уже созданные сделки живут со своим ключом —
 * иначе смена ключа разом обесценила бы все выданные приглашения.
 */
export function buildSetRefPubkey(pubkey: bigint, queryId = 0n): Cell {
  return beginCell()
    .storeUint(OP_SET_REF_PUBKEY, 32)
    .storeUint(queryId, 64)
    .storeUint(pubkey, 256)
    .endCell();
}

/**
 * Передача администрирования, шаг первый: предложение. `null` его отзывает.
 *
 * Права меняет не это сообщение, а согласие с той стороны — иначе опечатка в
 * адресе оставила бы протокол без админа навсегда: ни комиссии, ни реестров,
 * ни вывода собранного.
 */
export function buildSetNextAdmin(nextAdmin: string | null, queryId = 0n): Cell {
  return beginCell()
    .storeUint(OP_SET_NEXT_ADMIN, 32)
    .storeUint(queryId, 64)
    .storeAddress(nextAdmin === null ? null : Address.parse(nextAdmin))
    .endCell();
}

/** Шаг второй: согласие. Отправляется тем, кому предложили, и только им. */
export function buildAcceptAdmin(queryId = 0n): Cell {
  return beginCell().storeUint(OP_ACCEPT_ADMIN, 32).storeUint(queryId, 64).endCell();
}

/**
 * Вывод собранной комиссии с jetton-кошелька мастера.
 *
 * До этой операции комиссия копилась и достать её было нечем: мастер не
 * делал переводов вовсе.
 */
export function buildWithdrawFees(opts: {
  to: string;
  amount: bigint;
  queryId?: bigint;
}): Cell {
  return beginCell()
    .storeUint(OP_WITHDRAW_FEES, 32)
    .storeUint(opts.queryId ?? 0n, 64)
    .storeAddress(Address.parse(opts.to))
    .storeCoins(opts.amount)
    .endCell();
}

/**
 * Код будущих сделок. §9.8: уже идущие сделки живут своим кодом, мастер до
 * них не дотягивается — и это не недостаток, а единственный способ не
 * менять правила людям посреди выплат.
 */
export function buildSetDealCode(dealCode: Cell, queryId = 0n): Cell {
  return beginCell()
    .storeUint(OP_SET_DEAL_CODE, 32)
    .storeUint(queryId, 64)
    .storeRef(dealCode)
    .endCell();
}
